import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * useFormsTabs — RC-162b.
 *
 * Shared sub-nav state for the /forms/* surface. Every top-level Forms
 * page (Library, To Do, Designer index) calls this hook and spreads
 * the returned values into V2PageShell's tabs / activeTabId /
 * onTabChange props. The shell handles the visual rendering via its
 * built-in TabNav (no per-page custom strip).
 *
 * Why a hook (not a wrapper component):
 *   • V2PageShell already renders TabNav natively when given tabs[].
 *     A wrapper component would force every page to nest under it and
 *     remove control of the shell's own title/actions/back-button
 *     props. The hook just feeds the existing prop slots.
 *   • Active-tab derivation is route-aware (useLocation). Putting that
 *     logic in a hook means each page renders zero extra DOM and stays
 *     authoritative on its own title + actions.
 *   • Navigation is route-driven, not state-driven. Tab clicks route
 *     to the canonical URL; deep links + browser back/forward continue
 *     to highlight the correct tab without any local state.
 *
 * Tab map (every key matches the Forms RBAC route gate in App.jsx):
 *   library  → /forms/library  (also active for the bare /forms landing)
 *   to-do    → /forms/to-do
 *   designer → /forms/designer (the picker index; the editor at
 *                               /forms/design?wid=&nid= ALSO highlights
 *                               this tab so the strip stays anchored
 *                               while the operator drills in)
 *
 * @returns {{
 *   tabs: Array<{id:string,label:string}>,
 *   activeTabId: 'library'|'to-do'|'designer',
 *   onTabChange: (id:string) => void,
 * }}
 */
export default function useFormsTabs() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // 2026-07-04 — "To Do" tab retired per operator directive: it duplicated
  // the /my-tasks surface (RC-227 unified assignee inbox) which the RC-227
  // header comment already declared "the PRIMARY assignee surface; /forms
  // (below) stays as the operator/library surface for authoring +
  // cataloguing forms." My Tasks now owns 100% of the "waiting on me" model.
  // /forms/to-do the list route redirects to /my-tasks for back-compat
  // (App.jsx). The item-level fill page /forms/to-do/:id stays put — that
  // URL is the notification-email deep-link target, killing it would break
  // outbound assignee emails already in flight.
  const tabs = useMemo(() => ([
    { id: 'library',  label: 'Library'  },
    { id: 'designer', label: 'Designer' },
  ]), []);

  // Route → tab. /forms/design (editor) maps to the Designer tab too
  // so an author who deep-linked into the editor sees the strip with
  // Designer highlighted — gives them a one-click escape back to the
  // index without losing their place in the navigation model.
  const activeTabId = useMemo(() => {
    if (pathname.startsWith('/forms/designer') ||
        pathname.startsWith('/forms/design'))  return 'designer';
    return 'library';
  }, [pathname]);

  const onTabChange = useCallback((id) => {
    if (id === activeTabId) return;            // no-op click on active tab
    switch (id) {
      case 'designer': navigate('/forms/designer'); break;
      case 'library':
      default:         navigate('/forms/library');  break;
    }
  }, [activeTabId, navigate]);

  return { tabs, activeTabId, onTabChange };
}
